// src/components/formBuilder/VersionsDrawer.jsx
import React from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Divider,
  Chip,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SkeletonList from "./SkeletonList";
import axios from "axios";
import { auth } from "../../firebase-config";

const LIST_VERSIONS_URL =
  process.env.REACT_APP_LIST_FORM_VERSIONS_URL ||
  "https://listformversions-774073159618.us-central1.run.app";

export default function VersionsDrawer({
  open,
  onClose,
  template,           // { id, name }
  onSelectVersion,    // (version) => void
}) {
  const [loading, setLoading] = React.useState(false);
  const [versions, setVersions] = React.useState([]);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!open || !template?.id) return;
      try {
        setLoading(true);
        setError(null);
        const idToken = await auth.currentUser.getIdToken(true);
        const { data } = await axios.post(
          LIST_VERSIONS_URL,
          { formId: template.id },
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        if (!mounted) return;
        setVersions(data?.versions || []);
      } catch (e) {
        if (!mounted) return;
        setError(e?.message || "Failed to load versions.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [open, template?.id]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 420 } }}>
      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Versions — {template?.name || "Form"}
        </Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Divider />

      <Box sx={{ p: 2 }}>
        {loading && <SkeletonList rows={8} />}
        {!loading && error && (
          <Typography color="error" variant="body2">{error}</Typography>
        )}
        {!loading && !error && (
          <List dense>
            {versions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No versions found.
              </Typography>
            ) : (
              versions.map(v => (
                <ListItemButton key={v.id} onClick={() => onSelectVersion?.(v)}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>v{v.versionNumber || v.id}</span>
                        {v.status === "published" && <Chip size="small" color="success" label="Published" />}
                        {v.status === "draft" && <Chip size="small" label="Draft" />}
                      </Stack>
                    }
                    secondary={
                      v.updatedAt
                        ? `Updated: ${new Date(v.updatedAt).toLocaleString()}`
                        : v.createdAt
                        ? `Created: ${new Date(v.createdAt).toLocaleString()}`
                        : "—"
                    }
                  />
                </ListItemButton>
              ))
            )}
          </List>
        )}
      </Box>
    </Drawer>
  );
}